import { type ComponentType, useSyncExternalStore } from 'react';

import type { ActivationBatch } from './state/activationProgressSlice';

export type ActivationPresentationHandlers = {
  activatedText?: (count: number) => string;
  failedText?: (count: number) => string;
  onPrimaryAction?: () => void;
  /** Polls the group's in-progress batches; the peripheral batch poller when omitted. */
  Poller?: ComponentType<{ batch: ActivationBatch }>;
  /**
   * Retries the supplied group's failed batches. On success, dispatch
   * `replaceRetriedBatches` (or remove batches with no replacement) so the
   * completed failure card does not remain visible. Reject only when retry
   * could not be started; the snackbar keeps the failed card and shows an error.
   */
  onRetryBatches?: (failedBatches: ActivationBatch[]) => Promise<void>;
};

const handlersByPresentationGroup = new Map<
  string,
  Map<symbol, ActivationPresentationHandlers>
>();
const listeners = new Set<() => void>();
let revision = 0;

const notifyListeners = (): void => {
  revision += 1;
  for (const listener of listeners) {
    listener();
  }
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getRevision = (): number => revision;

export const usePresentationHandlersRevision = (): number =>
  useSyncExternalStore(subscribe, getRevision, getRevision);

/** Runtime-only callbacks; Redux batches store serializable presentation data and the group key. */
export const registerPresentationHandlers = (
  presentationGroup: string,
  handlers: ActivationPresentationHandlers
): (() => void) => {
  const registrations =
    handlersByPresentationGroup.get(presentationGroup) ?? new Map();
  const registrationId = Symbol(presentationGroup);
  registrations.set(registrationId, handlers);
  handlersByPresentationGroup.set(presentationGroup, registrations);
  notifyListeners();

  return () => {
    const currentRegistrations =
      handlersByPresentationGroup.get(presentationGroup);
    if (!currentRegistrations?.delete(registrationId)) return;
    if (currentRegistrations.size === 0) {
      handlersByPresentationGroup.delete(presentationGroup);
    }
    notifyListeners();
  };
};

export const getPresentationHandlers = (
  presentationGroup?: string
): ActivationPresentationHandlers | undefined =>
  (() => {
    const registrations = presentationGroup
      ? handlersByPresentationGroup.get(presentationGroup)
      : undefined;
    let latest: ActivationPresentationHandlers | undefined;
    for (const handlers of registrations?.values() ?? []) {
      latest = handlers;
    }
    return latest;
  })();

export const clearPresentationHandlers = (): void => {
  handlersByPresentationGroup.clear();
  notifyListeners();
};
